/**
 * JWT utility functions for token management
 */

export interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

/**
 * Decode JWT token without verification (client-side only)
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    if (!payload) {
      return null;
    }
    const decoded = atob(payload.replaceAll('-', '+').replaceAll('_', '/'));
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    console.error('[JWT] Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Get token expiration time in milliseconds
 * @param token JWT token string
 * @returns Expiration timestamp in milliseconds or null if invalid
 */
export function getTokenExpiration(token: string): number | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  // Convert from seconds to milliseconds
  return payload.exp * 1000;
}

/**
 * Check if token is expired
 * @param token JWT token string
 * @returns true if token is expired or invalid
 */
export function isTokenExpired(token: string): boolean {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return true;
  }

  return Date.now() >= expiration;
}

/**
 * Get milliseconds until token expires
 * @param token JWT token string
 * @returns Milliseconds until expiration, 0 if expired or invalid
 */
export function getTimeUntilExpiration(token: string): number {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return 0;
  }

  const timeLeft = expiration - Date.now();
  return Math.max(0, timeLeft);
}

/**
 * Calculate when to schedule token refresh - Production-ready with aggressive timing
 *
 * Strategy (more aggressive to handle tab switching and browser throttling):
 * - Very short tokens (< 10s): Refresh immediately with 1s delay
 * - Short tokens (10s - 2min): Refresh at 50% of lifetime (earlier to handle throttling)
 * - Medium tokens (2min - 15min): Refresh at 40-50% of lifetime (much earlier)
 * - Long tokens (> 15min): Refresh at 50% of lifetime (earlier than before)
 *
 * This ensures tokens are refreshed well before expiry, accounting for:
 * - Browser tab throttling
 * - User switching tabs
 * - Network delays
 * - System sleep/wake
 *
 * @param token JWT token string
 * @returns Milliseconds until refresh should occur, or null if token is invalid/expired
 */
export function calculateRefreshTime(token: string): number | null {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    console.warn('[JWT] Cannot calculate refresh time - invalid token');
    return null;
  }

  const now = Date.now();
  const timeUntilExpiration = expiration - now;

  // Token already expired - trigger immediate refresh
  if (timeUntilExpiration <= 0) {
    console.warn('[JWT] Token already expired, triggering immediate refresh');
    // Immediate refresh with small delay (100ms)
    return 100;
  }

  // Token expires very soon (< 5s) - refresh immediately (100ms delay)
  if (timeUntilExpiration < 5000) {
    return 100;
  }

  // Very short tokens (5s - 10s): Refresh immediately (1 second delay)
  if (timeUntilExpiration < 10000) {
    return 1000;
  }

  // Short tokens (10s - 2min): Refresh at 50% of lifetime (earlier)
  if (timeUntilExpiration < 120000) {
    const refreshTime = Math.floor(timeUntilExpiration * 0.5);
    return Math.max(1000, refreshTime);
  }

  // Medium tokens (2min - 15min): Refresh at 40-50% of lifetime with jitter (much earlier)
  if (timeUntilExpiration < 900000) {
    // Forty-five percent baseline (was 65%)
    const basePercent = 0.45;
    // Plus or minus 5% jitter
    const jitter = (Math.random() * 0.1) - 0.05;
    const refreshPercent = basePercent + jitter;
    const refreshTime = Math.floor(timeUntilExpiration * refreshPercent);
    return Math.max(1000, refreshTime);
  }

  // Long tokens (> 15min): Refresh at 50% of lifetime (earlier than before)
  const refreshTime = Math.floor(timeUntilExpiration * 0.5);
  return Math.max(1000, refreshTime);
}

/**
 * Check if token needs immediate refresh (expires in < 2 minutes)
 * Used for proactive refresh on tab visibility and user activity
 */
export function needsImmediateRefresh(token: string): boolean {
  const timeLeft = getTimeUntilExpiration(token);
  // Less than 2 minutes
  return timeLeft < 120000;
}
